import React, { useEffect, useState } from 'react';
import { Radio } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Tooltip } from 'react-tooltip';
import Notification from '../../components/notification/notification';
import MasterCard from '../../assets/mastercard.svg';
import Visa from '../../assets/visa.svg';
import AmericanExpress from '../../assets/american-express.svg';
import Discover from '../../assets/discover.svg';
import CreditCardSvg from '../../assets/credit-card-icon.svg';
import ModalDelete from '../../components/modal-delete/modal-delete';
import Modal from '../../components/modal/modal';
import NoCreditCard from '../../assets/no-credit-card.svg';
import NoBankAccount from '../../assets/no-bank-account.svg';
import { useSelector } from 'react-redux';
import bankAccountApi from '../../services/bankAccountApi';
import cardApi from '../../services/cardApi';
import { getSubString } from '../../utils/getSubString';
import InputMask from 'react-input-mask';
import { PlusIcon } from '@heroicons/react/20/solid';
import {
  CreditCardIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import BankAccountsSkeleton from './components/bank-accounts-skeleton';
import { PageHeader, TWButton, TWModal } from '../../components/ui';
import './bank-accounts.css';
import AddressAutocomplete from '../../components/address-autocomplete/address-autocomplete';

dayjs.extend(customParseFormat);

const createInitialBankAccountForm = (companyId) => ({
  id: '',
  accounttype: 10,
  accountholdername: '',
  bankname: '',
  isdefault: false,
  routingnumber: '',
  accountnumber: '',
  confirmaccountnumber: '',
  companyid: companyId,
  reprensentativefullname: '',
  taxid: '',
  last4ssn: '',
  reprensentativebirthday: '',
  city: '',
  state: '',
  postalcode: '',
  apartment: '',
  address: '',
  phone: '',
});

const BankAccounts = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [bankAccountList, setBankAccountList] = useState([]);
  const [creditCardList, setCreditCardList] = useState([]);
  const [deletedBankAccount, setDeletedBankAccount] = useState({});
  const [deletedCreditCard, setDeletedCreditCard] = useState({});
  const [defaultBankAccount, setDefaultBankAccount] = useState({});
  const [defaultCreditCard, setDefaultCreditCard] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [bankAccountForm, setBankAccountForm] = useState(
    createInitialBankAccountForm(globalCompany.id),
  );
  const [creditCardForm, setCreditCardForm] = useState({
    id: '',
    isdefault: false,
    createcardmodel: {
      expyear: 0,
      expmonth: 0,
      number: '',
      cvc: '',
      expdate: '',
    },
    companyid: globalCompany.id,
  });

  const [dialogs, setDialogs] = useState({
    upsertBankAccount: false,
    upsertCreditCard: false,
    deleteBankAccount: false,
    deleteCreditCard: false,
    defaultCreditCard: false,
    defaultBankAccount: false,
  });

  const [errors, setErrors] = useState({
    name: false,
    state: false,
    statecode: false,
    displayorder: false,
    zipcode: false,
    companyid: false,
    accountholdername: false,
    routingnumber: false,
    accountnumber: false,
    expyear: false,
    expmonth: false,
    number: false,
    cvc: false,
    reprensentativefullname: false,
    taxid: false,
    last4ssn: false,
    reprensentativebirthday: false,
    confirmaccountnumber: false,
    city: false,
    postalcode: false,
    address: false,
    phone: false,
  });

  const getBankAccountList = async () => {
    try {
      const query = `?companyid=${globalCompany.id}`;
      const list = await bankAccountApi.list(query);
      if (list.data.data.length > 0) {
        setBankAccountList(list.data.data);
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        display: true,
        message: error?.response?.data?.Message || 'Error',
        type: 'error',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getCreditCardList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setCreditCardList([]);
    try {
      const query = `?companyid=${globalCompany.id}`;
      const list = await cardApi.list(query);
      if (list.data.data.length > 0) {
        setCreditCardList(list.data.data);
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const onFormChangeBankAccount = (val, key) => {
    if (key === 'taxid') {
      const numbersOnly = val.replace(/\D/g, '');

      let formattedValue = '';
      if (bankAccountForm.accounttype === 10) {
        // Company: Tax ID format XX-XXXXXXX
        for (let i = 0; i < numbersOnly.length && i < 9; i++) {
          if (i === 2) {
            formattedValue += '-';
          }
          formattedValue += numbersOnly[i];
        }
      } else {
        // Individual: SSN format XXX-XX-XXXX
        for (let i = 0; i < numbersOnly.length && i < 9; i++) {
          if (i === 3 || i === 5) {
            formattedValue += '-';
          }
          formattedValue += numbersOnly[i];
        }
      }
      val = formattedValue;
    }

    // Clear taxid when account type changes
    if (key === 'accounttype') {
      setBankAccountForm((prev) => ({ ...prev, [key]: val, taxid: '' }));
      return;
    }

    setBankAccountForm((prev) => ({ ...prev, [key]: val }));
  };

  const onFormChangeCreditCard = (val, key) => {
    setCreditCardForm((prev) => ({
      ...prev,
      createcardmodel: { ...prev.createcardmodel, [key]: val },
    }));
  };

  const onClickAddBankAccount = () => {
    setBankAccountForm(createInitialBankAccountForm(globalCompany.id));
    setDialogs((prev) => ({
      ...prev,
      upsertCreditCard: false,
      upsertBankAccount: true,
    }));
  };

  const onClickAddCreditCard = () => {
    setCreditCardForm({
      id: '',
      isdefault: false,
      createcardmodel: {
        expyear: 0,
        expmonth: 0,
        number: '',
        cvc: '',
        expdate: '',
      },
      companyid: globalCompany.id,
    });
    setDialogs((prev) => ({
      ...prev,
      upsertBankAccount: false,
      upsertCreditCard: true,
    }));
  };

  const onFormSubmitBankAccount = async () => {
    if (
      !bankAccountForm.companyid ||
      !bankAccountForm.accountholdername ||
      !bankAccountForm.routingnumber ||
      !bankAccountForm.accountnumber ||
      !bankAccountForm.taxid ||
      !bankAccountForm.last4ssn ||
      !bankAccountForm.reprensentativefullname ||
      !bankAccountForm.reprensentativebirthday ||
      !bankAccountForm.city ||
      !bankAccountForm.state ||
      !bankAccountForm.postalcode ||
      !bankAccountForm.address ||
      !bankAccountForm.phone ||
      bankAccountForm.accountnumber !== bankAccountForm.confirmaccountnumber
    ) {
      setErrors((prev) => ({
        ...prev,
        companyid: !bankAccountForm.companyid,
        accountholdername: !bankAccountForm.accountholdername,
        routingnumber: !bankAccountForm.routingnumber,
        accountnumber: !bankAccountForm.accountnumber,
        taxid: !bankAccountForm.taxid,
        last4ssn: !bankAccountForm.last4ssn,
        reprensentativefullname: !bankAccountForm.reprensentativefullname,
        reprensentativebirthday: !bankAccountForm.reprensentativebirthday,
        city: !bankAccountForm.city,
        state: !bankAccountForm.state,
        postalcode: !bankAccountForm.postalcode,
        address: !bankAccountForm.address,
        phone: !bankAccountForm.phone,
        confirmaccountnumber:
          bankAccountForm.accountnumber !==
          bankAccountForm.confirmaccountnumber,
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        companyid: false,
        accountholdername: false,
        routingnumber: false,
        accountnumber: false,
        taxid: false,
        last4ssn: false,
        reprensentativefullname: false,
        reprensentativebirthday: false,
        confirmaccountnumber: false,
        city: false,
        state: false,
        postalcode: false,
        address: false,
        phone: false,
      }));
    }

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));

      const body = { ...bankAccountForm };
      if (bankAccountList.length === 0) {
        body.isdefault = true;
      }
      await bankAccountApi.upsert(body);
    } catch (error) {
    } finally {
      await getBankAccountList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsertBankAccount: false }));
    }
  };
  const onFormSubmitCreditCard = async () => {
    if (creditCardForm.createcardmodel?.expdate) {
      if (creditCardForm.createcardmodel?.expdate.length > 5) {
        creditCardForm.createcardmodel.expdate =
          creditCardForm.createcardmodel.expdate.slice(0, 5);
      }

      const date = creditCardForm.createcardmodel.expdate.split('/');
      if (date.length === 2) {
        creditCardForm.createcardmodel.expmonth = date[0];
        creditCardForm.createcardmodel.expyear = date[1];
      }
    }

    if (
      !creditCardForm.companyid ||
      !creditCardForm.createcardmodel.expyear ||
      !creditCardForm.createcardmodel.expmonth ||
      !creditCardForm.createcardmodel.number ||
      !creditCardForm.createcardmodel.cvc ||
      creditCardForm.createcardmodel.number.length < 15 ||
      creditCardForm.createcardmodel.cvc.length < 3 ||
      creditCardForm.createcardmodel.zipcode.length < 5
    ) {
      setErrors((prev) => ({
        ...prev,
        companyid: !creditCardForm.companyid ? true : false,
        expyear: !creditCardForm.createcardmodel.expyear ? true : false,
        expmonth: !creditCardForm.createcardmodel.expmonth ? true : false,
        zipcode:
          !creditCardForm.createcardmodel.zipcode ||
          creditCardForm.createcardmodel.zipcode.length < 5
            ? true
            : false,
        number:
          !creditCardForm.createcardmodel.number ||
          creditCardForm.createcardmodel.number.length < 15
            ? true
            : false,
        cvc:
          !creditCardForm.createcardmodel.cvc ||
          creditCardForm.createcardmodel.cvc.length < 3
            ? true
            : false,
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        companyid: false,
        expyear: false,
        expmonth: false,
        number: false,
        cvc: false,
        zipcode: false,
      }));
    }

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));

      const body = { ...creditCardForm };
      if (creditCardList.length === 0) {
        body.isdefault = true;
      }
      await cardApi.upsert(body);
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        display: true,
        message: error?.response?.data?.Message || 'Error',
        type: 'error',
      }));
    } finally {
      await getCreditCardList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsertCreditCard: false }));
    }
  };

  const onCloseUpsertBankAccount = () => {
    setErrors((prev) => ({
      ...prev,
      name: false,
      state: false,
      statecode: false,
      displayorder: false,
      zipcode: false,
      companyid: false,
      accountholdername: false,
      routingnumber: false,
      accountnumber: false,
      expyear: false,
      expmonth: false,
      number: false,
      cvc: false,
      reprensentativefullname: false,
      taxid: false,
      last4ssn: false,
      reprensentativebirthday: false,
      confirmaccountnumber: false,
      city: false,
      postalcode: false,
      address: false,
      phone: false,
    }));
    setDialogs((prev) => ({ ...prev, upsertBankAccount: false }));
  };

  const onCloseUpsertCreditCard = () => {
    setErrors((prev) => ({
      ...prev,
      name: false,
      state: false,
      statecode: false,
      displayorder: false,
      zipcode: false,
      companyid: false,
      accountholdername: false,
      routingnumber: false,
      accountnumber: false,
      expyear: false,
      expmonth: false,
      number: false,
      cvc: false,
      reprensentativefullname: false,
      taxid: false,
      last4ssn: false,
      reprensentativebirthday: false,
      confirmaccountnumber: false,
      city: false,
      postalcode: false,
      address: false,
      phone: false,
    }));
    setDialogs((prev) => ({ ...prev, upsertCreditCard: false }));
  };

  const onClickDeleteBankAccount = (bankAccount) => {
    if (bankAccount.isdefault) {
      setNotf({
        display: true,
        type: 'error',
        message:
          'Default bank cannot be removed. Please add a new checking account and set it as default if needed. Contact customer support for assistance.',
      });
      return;
    }
    setDeletedBankAccount(bankAccount);
    setDialogs((prev) => ({ ...prev, deleteBankAccount: true }));
  };

  const onClickDeleteCreditCard = (creditCard) => {
    if (creditCard.isdefault) {
      setNotf({
        display: true,
        type: 'error',
        message:
          'Default credit card cannot be removed. Please add a new card and set it as default if needed. Contact customer support for assistance.',
      });
      return;
    }
    setDeletedCreditCard(creditCard);
    setDialogs((prev) => ({ ...prev, deleteCreditCard: true }));
  };

  const onConfirmDeleteBankAccount = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await bankAccountApi.deleteById(deletedBankAccount.id);
    } catch (error) {
    } finally {
      setDialogs((prev) => ({
        ...prev,
        deleteBankAccount: false,
        upsertBankAccount: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getBankAccountList();
    }
  };

  const onConfirmDeleteCreditCard = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await cardApi.deleteById(deletedCreditCard.id);
    } catch (error) {
    } finally {
      setDialogs((prev) => ({
        ...prev,
        deleteCreditCard: false,
        upsertCreditCard: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getCreditCardList();
    }
  };

  const onChangeDefaultBankAccount = async () => {
    const { account, val, index } = defaultBankAccount;

    if (val === false) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Cannot uncheck default bank account',
      });
      return;
    }
    setLoading((prev) => ({ ...prev, upsertLoading: true }));
    const defaultAccount = bankAccountList.find((x) => x.isdefault === true);
    try {
      setBankAccountList((prev) => {
        const newList = [...prev];
        newList[index].isdefault = val;
        return newList;
      });
      if (defaultAccount) {
        setBankAccountList((prev) => {
          const newList = [...prev];
          const defaultIndex = newList.findIndex(
            (x) => x.id === defaultAccount.id,
          );
          newList[defaultIndex].isdefault = false;
          return newList;
        });
        const body = { ...defaultAccount, isdefault: false };
        await bankAccountApi.upsert(body);
      }
      const body = { ...account, isdefault: val };
      await bankAccountApi.upsert(body);
    } catch (error) {
    } finally {
      setLoading((prev) => ({
        ...prev,
        upsertLoading: false,
        listLoading: true,
      }));
      setDialogs((prev) => ({ ...prev, defaultBankAccount: false }));
      await getBankAccountList();
    }
  };

  const onClickDefaultBankAccount = (account, val, index) => {
    setDefaultBankAccount({
      account,
      val,
      index,
    });
    setDialogs((prev) => ({ ...prev, defaultBankAccount: true }));
  };

  const onClickDefaultCreditCard = (card, val, index) => {
    setDefaultCreditCard({
      card,
      val,
      index,
    });
    setDialogs((prev) => ({ ...prev, defaultCreditCard: true }));
  };

  const onChangeDefaultCreditCard = async () => {
    const { card, val, index } = defaultCreditCard;

    if (val === false) {
      setNotf({
        display: true,
        type: 'error',
        message: 'Cannot uncheck default credit card',
      });
      return;
    }
    setLoading((prev) => ({ ...prev, upsertLoading: true }));
    const defaultCard = creditCardList.find((x) => x.isdefault === true);
    try {
      setCreditCardList((prev) => {
        const newList = [...prev];
        newList[index].isdefault = val;
        return newList;
      });
      if (defaultCard) {
        setCreditCardList((prev) => {
          const newList = [...prev];
          const defaultIndex = newList.findIndex(
            (x) => x.id === defaultCard.id,
          );
          newList[defaultIndex].isdefault = false;
          return newList;
        });
        const body = { ...defaultCard, isdefault: false };
        await cardApi.upsert(body);
      }
      const body = { ...card, isdefault: val };
      await cardApi.upsert(body);
    } catch (error) {
      setNotf({
        display: true,
        type: 'error',
        message: error?.response?.data?.Message || 'Error',
      });
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, defaultCreditCard: false }));
      await getCreditCardList();
    }
  };

  const cardImage = (brand) => {
    switch (brand) {
      case 'Visa':
        return Visa;
      case 'MasterCard':
        return MasterCard;
      case 'American Express':
        return AmericanExpress;
      case 'Discover':
        return Discover;
      default:
        return CreditCardSvg;
    }
  };

  const expriyFormat = (expdate) => {
    if (!expdate) return '';
    const expDateFormatter =
      expdate?.replace(/\//g, '').substring(0, 2) +
      (expdate?.length > 2 ? '/' : '') +
      expdate?.replace(/\//g, '').substring(2, 4);

    return expDateFormatter;
  };

  const onAddressSelect = (obj) => {
    setBankAccountForm((prev) => ({
      ...prev,
      address: obj.street,
      city: obj.city,
      state: obj.state,
      postalcode: obj.zipCode,
      longitude: obj?.longitude || '',
      latitude: obj?.latitude || '',
      apartment: obj?.apartment || '',
    }));
  };

  useEffect(() => {
    getCreditCardList();
    getBankAccountList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id]);

  return (
    <>
      <div className="bank-accounts-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <PageHeader
            title="Payments"
            description="Manage credit cards for charges and bank accounts for payouts."
          />

          {loading.listLoading ? (
            <BankAccountsSkeleton />
          ) : (
            <div className="space-y-10">
              <section className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6 dark:border-slate-700">
                  <div className="flex gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center">
                      <CreditCardIcon className="h-6 w-6 text-slate-900 dark:text-slate-100" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        Credit cards
                      </h2>
                      <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                        Cards used for payments and subscriptions.
                      </p>
                    </div>
                  </div>
                  <TWButton
                    variant="primary"
                    icon={PlusIcon}
                    label="Add Card"
                    disabled={loading.listLoading}
                    onClick={() => onClickAddCreditCard()}
                  />
                </div>

                <div className="p-5 sm:p-6">
                  {creditCardList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/50 py-14 dark:border-slate-600 dark:bg-slate-900/40">
                      <img
                        src={NoCreditCard}
                        alt=""
                        className="h-20 w-auto opacity-90"
                      />
                      <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                        No credit cards yet
                      </p>
                      <p className="mt-1 max-w-80 text-center text-xs text-slate-500 dark:text-slate-400">
                        Add a card to pay invoices when you need to.
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {creditCardList.map((card, cardIdx) => (
                        <li key={cardIdx}>
                          <div className="flex flex-col gap-4 rounded-md border border-slate-100 bg-slate-50/30 p-4 transition hover:border-slate-200 hover:bg-white sm:flex-row sm:items-center sm:justify-between dark:border-slate-700/80 dark:bg-slate-800/40 dark:hover:border-slate-600">
                            <div className="flex items-center gap-4">
                              <img
                                className="h-9 w-auto object-contain"
                                src={cardImage(card.brand)}
                                alt=""
                              />
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                  ···· {card.last4}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {card.brand || 'Card'}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {!card.isdefault ? (
                                <>
                                  <Tooltip id="setdefault" />
                                  <button
                                    type="button"
                                    data-tooltip-id="setdefault"
                                    data-tooltip-content="Set as default credit card"
                                    onClick={() =>
                                      onClickDefaultCreditCard(
                                        card,
                                        true,
                                        cardIdx,
                                      )
                                    }
                                    className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                                  >
                                    Set default
                                  </button>
                                </>
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                                  Default
                                </span>
                              )}
                              {!card.isdefault && (
                                <>
                                  <Tooltip id="delete" />
                                  <button
                                    type="button"
                                    data-tooltip-id="delete"
                                    data-tooltip-content="Remove this card"
                                    onClick={() =>
                                      onClickDeleteCreditCard(card)
                                    }
                                    className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 transition hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <section className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6 dark:border-slate-700">
                  <div className="flex gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center">
                      <CurrencyDollarIcon className="h-6 w-6 text-slate-900 dark:text-slate-100" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        Bank accounts
                      </h2>
                      <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                        Accounts used for payouts and deposits.
                      </p>
                    </div>
                  </div>
                  <TWButton
                    variant="primary"
                    icon={PlusIcon}
                    label="Add Bank Account"
                    disabled={loading.listLoading}
                    onClick={() => onClickAddBankAccount()}
                  />
                </div>

                <div className="p-5 sm:p-6">
                  {bankAccountList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/50 py-14 dark:border-slate-600 dark:bg-slate-900/40">
                      <img
                        src={NoBankAccount}
                        alt=""
                        className="h-20 w-auto opacity-90"
                      />
                      <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                        No bank accounts yet
                      </p>
                      <p className="mt-1 max-w-80 text-center text-xs text-slate-500 dark:text-slate-400">
                        Add a checking account to receive payouts.
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {bankAccountList.map((account, accountIdx) => (
                        <li key={accountIdx}>
                          <div className="flex flex-col gap-4 rounded-md border border-slate-100 bg-slate-50/30 p-4 transition hover:border-slate-200 hover:bg-white sm:flex-row sm:items-center sm:justify-between dark:border-slate-700/80 dark:bg-slate-800/40 dark:hover:border-slate-600">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {account.accountholdername}
                              </p>
                              <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                                {account.accounttype === 10
                                  ? 'Company'
                                  : 'Individual'}
                              </p>
                              {account.bankname && (
                                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                  {account.bankname}
                                </p>
                              )}
                              <p className="mt-0.5 font-mono text-sm font-semibold tracking-wide text-slate-800 dark:text-slate-200">
                                ···· {getSubString(account.accountnumber, 4)}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {!account.isdefault ? (
                                <>
                                  <Tooltip id="setdefaultaccount" />
                                  <button
                                    type="button"
                                    data-tooltip-id="setdefaultaccount"
                                    data-tooltip-content="Set as default payout account"
                                    onClick={() =>
                                      onClickDefaultBankAccount(
                                        account,
                                        true,
                                        accountIdx,
                                      )
                                    }
                                    className="inline-flex items-center rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                                  >
                                    Set default
                                  </button>
                                </>
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                                  Default
                                </span>
                              )}
                              {!account.isdefault && (
                                <>
                                  <Tooltip id="deleteAccount" />
                                  <button
                                    type="button"
                                    data-tooltip-id="deleteAccount"
                                    data-tooltip-content="Remove this bank account"
                                    onClick={() =>
                                      onClickDeleteBankAccount(account)
                                    }
                                    className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 transition hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              {bankAccountList.length > 0 && (
                <div className="reveal rounded-md border border-slate-200 bg-slate-50/80 px-4 py-4 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300 sm:px-5">
                  <p>
                    We automatically process payouts every{' '}
                    <strong>Monday</strong>; it may take up to{' '}
                    <strong>two business days</strong> to arrive. Public
                    holidays may delay transfers.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <TWModal
        open={dialogs.upsertBankAccount}
        onClose={onCloseUpsertBankAccount}
        title={bankAccountForm.id ? 'Edit bank account' : 'Add bank account'}
        size="2xl"
        showCloseButton
        panelClassName="dark:text-slate-200"
        bodyClassName="min-h-0 space-y-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 max-h-[60vh]"
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={() => onCloseUpsertBankAccount()}
              className="w-full sm:w-auto"
            />
            <TWButton
              variant="primary"
              label={
                bankAccountForm.id ? 'Save bank account' : 'Add bank account'
              }
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={() => onFormSubmitBankAccount()}
              className="w-full sm:w-auto"
            />
          </div>
        }
      >
        <div className="flex">
          <div className="grid pr-2 pb-1">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
              >
                Account Type*
              </label>
            </div>
            <div className="flex">
              <Radio.Group
                className="bank-accounts-radio"
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'accounttype')
                }
                value={bankAccountForm.accounttype}
              >
                <Radio value={10}>Company</Radio>
                <Radio value={20}>Individual</Radio>
              </Radio.Group>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-5">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Legal Name*
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.accountholdername}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'accountholdername')
                }
                type="text"
                name="name"
                id="name"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder={
                  bankAccountForm.accounttype === 10 ? 'Acme LLC' : 'John Doe'
                }
              />
              <div className="h-2 mt-1  text-xs">
                {errors.accountholdername && (
                  <p className="text-red-700">Legal Name can not be empty</p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              {bankAccountForm.accounttype === 10 ? 'Tax ID*' : 'SSN*'}
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.taxid}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'taxid')
                }
                maxLength={bankAccountForm.accounttype === 10 ? 10 : 11}
                type="text"
                name="taxid"
                id="taxid"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder={
                  bankAccountForm.accounttype === 10
                    ? '12-3456789'
                    : '123-45-6789'
                }
              />
              <div className="h-2 mt-1  text-xs">
                {errors.taxid && (
                  <p className="text-red-700">
                    {bankAccountForm.accounttype === 10 ? 'Tax ID' : 'SSN'} can
                    not be empty
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-5">
          <div>
            <label
              htmlFor="routingnumber"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Routing Number*
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.routingnumber}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'routingnumber')
                }
                type="number"
                name="routingnumber"
                id="routingnumber"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="1234567890"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.routingnumber && (
                  <p className="text-red-700">
                    Routing Number can not be empty
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="bankname"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Bank Name{' '}
              <span className="font-normal text-gray-500 dark:text-slate-500">
                (Optional)
              </span>
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.bankname}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'bankname')
                }
                type="text"
                name="bankname"
                id="bankname"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="Chase"
              />
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-5">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Account Number*
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.accountnumber}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'accountnumber')
                }
                type="number"
                name="accountnumber"
                id="accountnumber"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="1234567890"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.accountnumber && (
                  <p className="text-red-700">
                    Account Number can not be empty
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Confirm Account Number*
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.confirmaccountnumber}
                onChange={(e) =>
                  onFormChangeBankAccount(
                    e.target.value,
                    'confirmaccountnumber',
                  )
                }
                type="number"
                name="confirmaccountnumber"
                id="confirmaccountnumber"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="1234567890"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.confirmaccountnumber && (
                  <p className="text-red-700">Account Number does not match</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-bold text-gray-700 dark:text-slate-200"
          >
            Account Representative
          </label>
          <span className="text-xs text-gray-500 dark:text-slate-400">
            Payout needs to be activated by a person, The representative must
            either be an owner or an executive (manager,etc).
          </span>
        </div>
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-slate-400"
          >
            Representative Full Name*
          </label>
          <div className="mt-1">
            <input
              value={bankAccountForm.reprensentativefullname}
              onChange={(e) =>
                onFormChangeBankAccount(
                  e.target.value,
                  'reprensentativefullname',
                )
              }
              type="text"
              name="reprensentativefullname"
              id="reprensentativefullname"
              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
              placeholder="John Doe"
            />
            <div className="h-2 mt-1  text-xs">
              {errors.reprensentativefullname && (
                <p className="text-red-700">
                  Representative Full Name can not be empty
                </p>
              )}
            </div>
          </div>
        </div>
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-slate-400"
          >
            Phone*
          </label>
          <div className="mt-1">
            <InputMask
              value={bankAccountForm.phone}
              mask="(999) 999 9999"
              maskPlaceholder={' '}
              onChange={(e) => onFormChangeBankAccount(e.target.value, 'phone')}
              type="text"
              name="phone"
              id="phone"
              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
            />
            <div className="h-2 mt-1  text-xs">
              {errors.phone && (
                <p className="text-red-700">Phone number can not be empty</p>
              )}
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-5">
          <div>
            <label
              htmlFor="code"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Representative Birthday*
            </label>
            <div className="mt-1">
              <InputMask
                mask="99/99/9999"
                maskChar=""
                value={bankAccountForm.reprensentativebirthday}
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                onChange={(e) =>
                  onFormChangeBankAccount(
                    e.target.value,
                    'reprensentativebirthday',
                  )
                }
                placeholder="MM/DD/YYYY"
                alwaysShowMask
              />
              <div className="h-2 mt-1  text-xs">
                {errors.reprensentativebirthday && (
                  <p className="text-red-700">
                    Select a Representative Birthday
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Last 4 Digit of SSN*
            </label>
            <div className="mt-1">
              <input
                value={bankAccountForm.last4ssn}
                onChange={(e) =>
                  onFormChangeBankAccount(e.target.value, 'last4ssn')
                }
                maxLength={4}
                type="text"
                name="last4ssn"
                id="last4ssn"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="1234"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.last4ssn && (
                  <p className="text-red-700">
                    Last 4 Digit of SSN can not be empty
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-bold text-gray-700 dark:text-slate-200"
          >
            Legal Address
          </label>
          <span className="text-xs text-gray-500 dark:text-slate-400">
            Please enter the address for legal documents, including tax forms
            and 1099s, etc.
          </span>
        </div>
        <div className="grid sm:grid-cols-4 gap-x-5">
          <div className="col-span-4 sm:col-span-3 ">
            <label
              htmlFor="address"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400 "
            >
              Street Name
            </label>
            <AddressAutocomplete
              value={bankAccountForm.address}
              onChange={(e) => onAddressSelect(e)}
            />

            <div className="h-2 mt-1  text-xs">
              {errors.address ? (
                <p className="text-red-700">Address can not be empty</p>
              ) : (
                <p className="text-gray-500 dark:text-slate-500">
                  Please type your street name here e.g. '100 Main St'
                </p>
              )}
            </div>
          </div>
          <div className="col-span-4 sm:col-span-1">
            <label
              htmlFor="apartment"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Apt / Suite
            </label>
            <input
              value={bankAccountForm.apartment}
              onChange={(e) =>
                onFormChangeBankAccount(e.target.value, 'apartment')
              }
              type="text"
              name="apartment"
              id="apartment"
              className="block w-full appearance-none rounded-md border dark:text-slate-500 dark:placeholder:text-slate-500 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:border-slate-500"
              placeholder=""
            />
          </div>
        </div>
        <div className="col-span-8 grid grid-cols-8 gap-6 pb-5">
          <div className="col-span-8 sm:col-span-4">
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400 "
            >
              City
            </label>
            <input
              value={bankAccountForm.city}
              onChange={(e) => onFormChangeBankAccount(e.target.value, 'city')}
              type="text"
              name="city"
              id="city"
              className="block w-full appearance-none rounded-md border dark:text-slate-500 dark:placeholder:text-slate-500 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:border-slate-500"
              placeholder=""
            />
            <div className="h-2 mt-1  text-xs">
              {errors.city && (
                <p className="text-red-700">City can not be empty</p>
              )}
            </div>
          </div>
          <div className="col-span-8 sm:col-span-2">
            <label
              htmlFor="state"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400 "
            >
              State
            </label>
            <input
              value={bankAccountForm.state}
              onChange={(e) => onFormChangeBankAccount(e.target.value, 'state')}
              type="text"
              name="state"
              id="state"
              className="block w-full appearance-none rounded-md border dark:text-slate-500 dark:placeholder:text-slate-500 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:border-slate-500"
              placeholder=""
            />
            <div className="h-2 mt-1  text-xs">
              {errors.state && (
                <p className="text-red-700">State can not be empty</p>
              )}
            </div>
          </div>
          <div className="col-span-8 sm:col-span-2">
            <label
              htmlFor="postalcode"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400 dark:placeholder:text-slate-500"
            >
              Zip Code
            </label>
            <input
              value={bankAccountForm.postalcode}
              onChange={(e) =>
                onFormChangeBankAccount(e.target.value, 'postalcode')
              }
              type="text"
              name="postalcode"
              id="postalcode"
              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500 dark:border-slate-500"
              placeholder=""
            />
            <div className="h-2 mt-1  text-xs">
              {errors.postalcode && (
                <p className="text-red-700">Zip Code can not be empty</p>
              )}
            </div>
          </div>
        </div>
        <span className="text-xs text-gray-500 dark:text-slate-400">
          By clicking 'Add Bank Account' you agree to the terms in the{' '}
          <a
            className="font-medium text-blue-600 dark:text-zinc-200 hover:text-blue-500 dark:hover:text-sky-400"
            href="https://stripe.com/legal/connect-account"
            target="_blank"
            rel="noreferrer"
          >
            Stripe Agreement
          </a>{' '}
          and certify that the provided information is accurate and complete.
        </span>

        <div className="h-2 mt-1  text-xs"></div>
      </TWModal>
      <ModalDelete
        title="Delete Bank Account"
        text={`"Are you sure you want to remove this checking? Once removed, all information related to account ending in ${deletedBankAccount.accountnumber} will be deleted."`}
        loading={loading.deleteLoading}
        open={dialogs.deleteBankAccount}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, deleteBankAccount: false }))
        }
        onOk={onConfirmDeleteBankAccount}
        item={{
          name: deletedBankAccount.accountnumber,
          id: deletedBankAccount.id,
        }}
      />
      <TWModal
        open={dialogs.upsertCreditCard}
        onClose={onCloseUpsertCreditCard}
        title="Add credit card"
        size="lg"
        showCloseButton
        panelClassName="dark:text-slate-200"
        bodyClassName="min-h-0 space-y-5 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 max-h-[60vh]"
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={() => onCloseUpsertCreditCard()}
              className="w-full sm:w-auto"
            />
            <TWButton
              variant="primary"
              label="Add credit card"
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={() => onFormSubmitCreditCard()}
              className="w-full sm:w-auto"
            />
          </div>
        }
      >
        <div className="mt-2 -space-y-px rounded-md bg-white dark:bg-slate-800 shadow-sm">
          <div>
            <label
              htmlFor="cc-number"
              className="block pb-3 text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Card Details*
            </label>
            <input
              value={creditCardForm.createcardmodel?.number}
              onChange={(e) => onFormChangeCreditCard(e.target.value, 'number')}
              type="text"
              maxLength={16}
              minLength={15}
              name="cc-number"
              autoComplete="cc-number"
              id="cc-number"
              className="focus:z-10 pl-2 relative block w-full rounded-md rounded-b-none border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
              placeholder="Card number"
            />
          </div>
          <div className="flex -space-x-px">
            <div className="w-1/2 min-w-0 flex-1">
              <label htmlFor="card-expiration-date" className="sr-only">
                Expiration date
              </label>
              <input
                value={expriyFormat(creditCardForm.createcardmodel.expdate)}
                onChange={(e) => {
                  onFormChangeCreditCard(e.target.value, 'expdate');
                }}
                type="text"
                autoComplete="cc-exp"
                name="card-expiration-date"
                id="card-expiration-date"
                className="focus:z-10 pl-2 relative block w-full rounded-md rounded-t-none rounded-r-none border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="MM / YY"
              />
            </div>
            <div className="min-w-0 flex-1">
              <label htmlFor="card-cvc" className="sr-only">
                CVC
              </label>
              <input
                value={creditCardForm.createcardmodel?.cvc}
                onChange={(e) => onFormChangeCreditCard(e.target.value, 'cvc')}
                type="text"
                autoComplete="cc-csc"
                name="cvc"
                id="cvc"
                maxLength={4}
                className="focus:z-10 pl-2 relative block w-full rounded-md rounded-t-none rounded-l-none border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="CVC"
              />
            </div>
          </div>
        </div>
        <div>
          {errors.number && (
            <div className="h-2 mt-1 text-xs">
              <p className="text-red-700">
                *Please enter a valid credit card number
              </p>
            </div>
          )}
          {errors.expyear && (
            <div className="h-2 mt-1  text-xs">
              <p className="text-red-700">*Expyear can not be empty</p>
            </div>
          )}
          {errors.cvc && (
            <div className="h-2 mt-1  text-xs">
              <p className="text-red-700">*Please enter a valid CVC</p>
            </div>
          )}
        </div>
        <div className="space-x-2">
          <div className="mt-2  -space-y-px rounded-md">
            <label
              htmlFor="email"
              className="block pb-3 text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Billing address*
            </label>
            <div>
              <label htmlFor="country" className="sr-only">
                Country
              </label>
              <select
                id="country"
                name="country"
                autoComplete="country-name"
                className="focus:z-10 pl-2 relative block w-full rounded-md rounded-b-none border bg-white dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
              >
                <option>United States</option>
              </select>
            </div>
            <div>
              <label htmlFor="postal-code" className="sr-only">
                ZIP / Postal code*
              </label>
              <input
                value={creditCardForm.createcardmodel?.zipcode}
                onChange={(e) =>
                  onFormChangeCreditCard(e.target.value, 'zipcode')
                }
                type="text"
                name="zipcode"
                id="zipcode"
                maxLength={5}
                autoComplete="postal-code"
                className="focus:z-10 pl-2 relative block w-full rounded-md rounded-t-none border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="ZIP / Postal code"
              />
            </div>
            <span className="text-xs text-gray-500 dark:text-slate-400">
              By clicking 'Add Credit Card' you agree{' '}
              <a
                className="font-medium text-blue-600 dark:text-zinc-200 hover:text-blue-500 dark:hover:text-sky-400"
                href="https://stripe.com/legal/ssa"
                target="_blank"
                rel="noreferrer"
              >
                Stripe Terms.
              </a>
            </span>
            <div className="mt-1 text-xs">
              {errors.zipcode && (
                <p className="text-red-700">*Please enter a valid zip code</p>
              )}
            </div>
          </div>
        </div>
      </TWModal>
      <ModalDelete
        title="Delete Credit Card"
        text={`Are you sure you want to remove this credit card? Once removed, all information related to card ending in ${deletedCreditCard.last4} will be deleted`}
        loading={loading.deleteLoading}
        open={dialogs.deleteCreditCard}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, deleteCreditCard: false }))
        }
        onOk={onConfirmDeleteCreditCard}
        item={{
          name: deletedCreditCard.last4,
          id: deletedCreditCard.id,
        }}
      />
      <Modal
        title="Make Default Payment Option?"
        text={`Are you sure you want to make this credit card your default payment option? This means that this card will be automatically charged for future invoices unless you choose a different payment method. You can still change this later.`}
        loading={loading.upsertLoading}
        open={dialogs.defaultCreditCard}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, defaultCreditCard: false }))
        }
        item={{
          name: defaultCreditCard.card?.last4,
          id: defaultCreditCard.card?.id,
        }}
        onOk={onChangeDefaultCreditCard}
      />
      <Modal
        title="Make Default Payment Option?"
        text={`Are you sure you want to make this account your default payment option? This means that this account will be automatically deposit for future payouts unless you choose a different payment method. You can still change this later.`}
        loading={loading.upsertLoading}
        open={dialogs.defaultBankAccount}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, defaultBankAccount: false }))
        }
        item={{
          name: defaultBankAccount.account?.accountholdername,
          id: defaultBankAccount.account?.id,
        }}
        onOk={onChangeDefaultBankAccount}
      />
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </>
  );
};

export default BankAccounts;
